// estimateUtils.js
import nomHsCodes from "./nomHsCodes.json";

const normalizeHsCode = (value) => String(value || "").replace(/\D/g, "");

const matchesHsCodePrefixes = (items = [], prefixes = []) => {
    const normalizedPrefixes = prefixes
        .map(normalizeHsCode)
        .filter(prefix => prefix.length === 4 || prefix.length === 6);

    return items.some((item) => {
        const hsCode = normalizeHsCode(item.hsCode || item.itemHSCode);
        return normalizedPrefixes.some(prefix => hsCode.slice(0, prefix.length) === prefix);
    });
};

const getNomHsCodePrefixes = () => [
    ...(nomHsCodes.fourDigit || []),
    ...(nomHsCodes.sixDigit || [])
];

const isNomService = (service) => service?.description?.trim().toLowerCase() === "nom";
const HS_CODE_STARTS_WITH_KEYS = ["HSCode Starts with", "Shipment HS Code Starts With"];

const findHsCodeStartsWithIndex = (values = []) =>
    values.findIndex((value) => HS_CODE_STARTS_WITH_KEYS.includes(value));

export const calculateTotalVolumetricWeight = (dimensions) => {
    return dimensions.reduce((total, dim) => {
        let length = parseFloat(dim.length.value) || 0;
        let width = parseFloat(dim.width.value) || 0;
        let height = parseFloat(dim.height.value) || 0;
        const quantity = parseFloat(dim.quantity.value) || 1;

        // Conversion logic: Convert Inches to CM if unit is 'in'
        // This mirrors the logic: convertInchesToCm = (inches) => inches * 2.54;
        if (dim.length.unit === 'in') length = length * 2.54;
        if (dim.width.unit === 'in') width = width * 2.54;
        if (dim.height.unit === 'in') height = height * 2.54;

        // Formula: (Length_cm * Width_cm * Height_cm) / 6000
        const volumetricWeightPerItem = (length * width * height) / 6000;
        return total + (volumetricWeightPerItem * quantity);
    }, 0);
};

export const evaluateSimpleCondition = (formula,
    values,
    calculatedValues,
    totalItemsValue,
    totalDimensionsWeight,
    totalDimensionsLength,
    totalDimensionsWidth,
    totalDimensionsHeight,
    cif,
    insurance,
    totalItemsQuantity,
    itemsTotalQty) => {
    // console.log('Starting evaluateSimpleCondition with:', {
    //     formula,
    //     values: JSON.stringify(values),
    //     calculatedValues: JSON.stringify(calculatedValues)
    // });

    // console.log('🔸 evaluateSimpleCondition ENTRY');
    // console.log('🔸 Formula received:', formula);
    // console.log('🔸 evaluateSimpleCondition ENTRY');

    const getDynamicValue = (valueKey) => {
        switch (valueKey.toLowerCase()) {
            case "document":
                // console.log("Inside evaluateSimpleCondition ", cif);

                return values.requiredDoc === 'Required' ? true : false;

            case "handling":  // ADD THIS NEW CASE
                return values.handling === 'YES' ? true : false;
            case "shipment value":
            case "invoice value":
                return totalItemsValue;
            case "quantity":
                return totalItemsQuantity;
            case "shipment weight":
            case "item weight":
                return totalDimensionsWeight;
            case "total item quantity":
                return itemsTotalQty;
            case "Volume Weight":
                // calculateTotalVolumetricWeight now handles unit conversion internally
                const totalVolumetricWeight = calculateTotalVolumetricWeight(values.dimensions);
                // console.log(totalVolumetricWeight, 'totalVolumetricWeight');
                return Math.max(totalDimensionsWeight, totalVolumetricWeight);
            case "Airfreight Volume":
                return calculateTotalVolumetricWeight(values.dimensions);
            case "Airfreight Charges":
                const chargeableWeight = calculateTotalVolumetricWeight(values.dimensions);
                const volumeWeight = Math.max(totalDimensionsWeight, chargeableWeight);
                if (volumeWeight < 360) {
                    return 6.50 * volumeWeight + 250;
                } else if (volumeWeight >= 360 && volumeWeight <= 1080) {
                    return 5.00 * volumeWeight + 250;
                } else {
                    return 4.00 * volumeWeight + 250;
                }
            case "cif":
                return cif;
            case "cif110":
                return totalItemsValue * 1.10;
            case "insurance":
                return insurance;
            case "hscode count":
                return getUniqueHSCodeCount(values.items);
            default:
                return calculatedValues[valueKey] !== undefined ?
                    calculatedValues[valueKey] :
                    parseFloat(valueKey) || 0;
        }
    };

    let index = 0;
    while (index < formula.length) {
        if (formula[index] === 'if') {
            index++;

            // Handle condition based on type
            const conditionType = formula[index].toLowerCase();

            // Boolean condition check (like 'document')
            if (['document', 'handling'].includes(conditionType)) {
                const conditionValue = getDynamicValue(conditionType);
                index++;

                // Find 'then' keyword
                while (index < formula.length && formula[index] !== 'then') {
                    index++;
                }

                if (index < formula.length && formula[index] === 'then') {
                    index++; // Move past 'then'

                    // Get the value after 'then'
                    const thenValue = getDynamicValue(formula[index]);

                    // If condition is true, return the 'then' value
                    if (conditionValue === true) {
                        // console.log(`Condition '${conditionType}' is true, returning ${thenValue}`);
                        return thenValue;
                    }

                    // Move index to look for 'else'
                    while (index < formula.length && formula[index] !== 'else') {
                        index++;
                    }

                    if (index < formula.length && formula[index] === 'else') {
                        index++; // Move past 'else'

                        // Skip optional 'then' after 'else'
                        if (index < formula.length && formula[index] === 'then') {
                            index++;
                        }

                        // Get the value after 'else' or 'else then'
                        const elseValue = getDynamicValue(formula[index]);

                        // If condition is false, return the 'else' value
                        if (conditionValue === false) {
                            // console.log(`Condition '${conditionType}' is false, returning ${elseValue}`);
                            return elseValue;
                        }
                    }
                }
            }
            // Numeric comparison conditions (like value > number)
            else {
                const leftValue = getDynamicValue(formula[index]);
                index++;

                if (index < formula.length && ['>', '<', '>=', '<=', '=='].includes(formula[index])) {
                    const operator = formula[index];
                    index++;
                    const rightValue = getDynamicValue(formula[index]);
                    index++;

                    let conditionMet = false;
                    switch (operator) {
                        case '>': conditionMet = leftValue > rightValue; break;
                        case '<': conditionMet = leftValue < rightValue; break;
                        case '>=': conditionMet = leftValue >= rightValue; break;
                        case '<=': conditionMet = leftValue <= rightValue; break;
                        case '==': conditionMet = leftValue === rightValue; break;
                    }

                    // console.log(`Condition: ${leftValue} ${operator} ${rightValue}, Result: ${conditionMet}`);

                    // Find 'then' token
                    while (index < formula.length && formula[index] !== 'then') {
                        index++;
                    }

                    if (index < formula.length && formula[index] === 'then') {
                        index++; // Move past 'then'

                        // Get the value after 'then'
                        const thenValue = getDynamicValue(formula[index]);

                        // If condition is met, return the 'then' value
                        if (conditionMet) {
                            // console.log(`Numeric condition met, returning ${thenValue}`);
                            return thenValue;
                        }

                        // Move index to look for 'else'
                        while (index < formula.length && formula[index] !== 'else') {
                            index++;
                        }

                        if (index < formula.length && formula[index] === 'else') {
                            index++; // Move past 'else'

                            // Skip optional 'then' after 'else'
                            if (index < formula.length && formula[index] === 'then') {
                                index++;
                            }

                            // Get the value after 'else' or 'else then'
                            const elseValue = getDynamicValue(formula[index]);

                            // If condition is not met, return the 'else' value
                            if (!conditionMet) {
                                // console.log(`Numeric condition not met, returning ${elseValue}`);
                                return elseValue;
                            }
                        }
                    }
                }
            }
        }
        index++;
    }

    // Default return if no matching condition found
    // console.log('No matching condition found, returning 0');
    return 0;
};

export const evaluateConditionalFormula = (formula,
    values,
    calculatedValues,
    totalItemsValue,
    totalDimensionsWeight,
    totalDimensionsLength,
    totalDimensionsWidth,
    totalDimensionsHeight,
    cif,
    insurance,
    totalItemsQuantity,
    itemsTotalQty) => {
    // console.log('🔍 evaluateConditionalFormula ENTRY');
    // console.log('Formula received:', formula);
    // console.log('🔍 evaluateConditionalFormula ENTRY');
    // console.log('Starting evaluateConditionalFormula with:', {
    //     formula,
    //     values: JSON.stringify(values),
    //     calculatedValues: JSON.stringify(calculatedValues)
    // });

    const getDynamicValue = (valueKey) => {
        switch (valueKey.toLowerCase()) {
            case "shipment value":
            case "invoice value":
            case "invoice":
                // console.log(totalItemsValue, 'totalItemsValue');
                return totalItemsValue;
            case "quantity":
                return totalItemsQuantity;
            case "shipment weight":
            case "item weight":
                return totalDimensionsWeight;
            case "total item quantity":
                return itemsTotalQty;
            case "Volume Weight":
                const totalVolumetricWeight = calculateTotalVolumetricWeight(values.dimensions);
                // console.log(totalVolumetricWeight, 'totalVolumetricWeight');
                return Math.max(totalDimensionsWeight, totalVolumetricWeight);
            case "Airfreight Volume":
                return calculateTotalVolumetricWeight(values.dimensions);
            case "Airfreight Charges":
                const chargeableWeight = calculateTotalVolumetricWeight(values.dimensions);
                const volumeWeight = Math.max(totalDimensionsWeight, chargeableWeight);
                if (volumeWeight < 360) {
                    return 6.50 * volumeWeight + 250;
                } else if (volumeWeight >= 360 && volumeWeight <= 1080) {
                    return 5.00 * volumeWeight + 250;
                } else {
                    return 4.00 * volumeWeight + 250;
                }
            case "cif":
                return cif;
            case "cif110":
                return totalItemsValue * 1.10;
            case "insurance":
                return insurance;
            case "hscode count":
                return getUniqueHSCodeCount(values.items);
            case "document":
                return values.requiredDoc === 'Required' ? true : false;
            case "handling":  // ADD THIS NEW CASE
                return values.handling === 'YES' ? true : false;
            case "value":  // Add this line to handle the "Value" part
                return 0;  // Return 0 for standalone "Value" token
            default:
                return calculatedValues[valueKey] !== undefined ?
                    calculatedValues[valueKey] :
                    parseFloat(valueKey) || 0;
        }
    };

    // Process max/min function with comma-separated expressions
    const evaluateMinMax = (func, expressions) => {
        // console.log('🎯 evaluateMinMax CALLED');
        // console.log(`🎯 Function: ${func}`);
        // console.log('🎯 Expressions received:', expressions);

        const results = expressions.map(expr => {
            // console.log(`🎯 Processing expression: "${expr}"`);
            let tokens = expr.trim().split(' ').filter(t => t);
            // console.log('🎯 Original tokens:', tokens);

            // Handle multi-word tokens like "Invoice Value"
            const processedTokens = [];
            for (let i = 0; i < tokens.length; i++) {
                if (tokens[i] === 'Invoice' && tokens[i + 1] === 'Value') {
                    processedTokens.push('Invoice Value');
                    i++; // Skip the next token
                } else if (tokens[i] === 'Shipment' && tokens[i + 1] === 'Value') {
                    processedTokens.push('Shipment Value');
                    i++; // Skip the next token
                } else {
                    processedTokens.push(tokens[i]);
                }
            }

            // console.log('🎯 Processed tokens:', processedTokens);

            let result = 0;
            let operator = '+';

            for (let i = 0; i < processedTokens.length; i++) {
                if (['+', '-', '*', '/', '%'].includes(processedTokens[i])) {
                    operator = processedTokens[i];
                    // console.log(`🎯 Operator found: ${operator}`);
                } else {
                    const value = getDynamicValue(processedTokens[i]);
                    // console.log(`🎯 Token: ${processedTokens[i]}, Value: ${value}, Operator: ${operator}`);

                    switch (operator) {
                        case '+': result += value; break;
                        case '-': result -= value; break;
                        case '*': result *= value; break;
                        case '/': result /= value !== 0 ? value : 1; break;
                        case '%': result = (result * value) / 100; break;
                    }
                    // console.log(`🎯 Result after operation: ${result}`);
                }
            }
            // console.log(`🎯 Final expression result: ${result}`);
            return result;
        });

        // console.log('🎯 All results:', results);
        const finalResult = func === 'min' ? Math.min(...results) : Math.max(...results);
        // console.log(`🎯 Final ${func} result:`, finalResult);
        return finalResult;
    };

    // Find if/else-if/else blocks
    let index = 0;
    while (index < formula.length) {
        if (formula[index] === 'if') {
            // Parse condition
            index++;
            const conditionType = formula[index].toLowerCase();

            // Handle document type condition
            if (conditionType === 'document') {
                const isDocument = getDynamicValue('document');
                index++;

                // Find 'then' token
                while (index < formula.length && formula[index] !== 'then') {
                    index++;
                }

                if (index < formula.length && formula[index] === 'then') {
                    index++;
                    // Handle then case - find function (max/min)
                    if (['max', 'min'].includes(formula[index])) {
                        const func = formula[index];
                        index++;

                        // Find opening parenthesis
                        while (index < formula.length && formula[index] !== '(') {
                            index++;
                        }
                        index++; // Skip '('

                        // Collect expressions until closing parenthesis
                        const expressions = [];
                        let currentExpr = '';
                        let parenCount = 1;

                        while (index < formula.length && parenCount > 0) {
                            if (formula[index] === '(') parenCount++;
                            else if (formula[index] === ')') parenCount--;

                            if (formula[index] === ',' && parenCount === 1) {
                                expressions.push(currentExpr);
                                currentExpr = '';
                            } else if (parenCount > 0) {
                                currentExpr += formula[index] + ' ';
                            }
                            index++;
                        }
                        if (currentExpr) expressions.push(currentExpr);

                        if (isDocument) {
                            return evaluateMinMax(func, expressions);
                        }
                    }
                }

                // If condition false, find 'else' token
                while (index < formula.length && formula[index] !== 'else') {
                    index++;
                }

                if (index < formula.length && formula[index] === 'else') {
                    index++;
                    // Skip 'then' if it exists after else
                    if (index < formula.length && formula[index] === 'then') {
                        index++;
                    }

                    // Handle else case
                    if (['max', 'min'].includes(formula[index])) {
                        const func = formula[index];
                        index++;

                        // Find opening parenthesis
                        while (index < formula.length && formula[index] !== '(') {
                            index++;
                        }
                        index++; // Skip '('

                        // Collect expressions until closing parenthesis
                        const expressions = [];
                        let currentExpr = '';
                        let parenCount = 1;

                        while (index < formula.length && parenCount > 0) {
                            if (formula[index] === '(') parenCount++;
                            else if (formula[index] === ')') parenCount--;

                            if (formula[index] === ',' && parenCount === 1) {
                                expressions.push(currentExpr);
                                currentExpr = '';
                            } else if (parenCount > 0) {
                                currentExpr += formula[index] + ' ';
                            }
                            index++;
                        }
                        if (currentExpr) expressions.push(currentExpr);

                        if (!isDocument) {
                            return evaluateMinMax(func, expressions);
                        }
                    }
                }
            }
            // Handle numeric comparison conditions (e.g., 'Invoice Value' > 5000)
            else {
                const leftValue = getDynamicValue(formula[index]);
                index++;
                const operator = formula[index];
                index++;
                const rightValue = getDynamicValue(formula[index]);
                index++;

                let conditionMet = false;
                switch (operator) {
                    case '>': conditionMet = leftValue > rightValue; break;
                    case '<': conditionMet = leftValue < rightValue; break;
                    case '>=': conditionMet = leftValue >= rightValue; break;
                    case '<=': conditionMet = leftValue <= rightValue; break;
                    case '==': conditionMet = leftValue === rightValue; break;
                }

                // console.log(`Condition: ${formula[index - 3]} ${operator} ${formula[index - 1]}, Result: ${conditionMet}`);

                // Find 'then' token
                while (index < formula.length && formula[index] !== 'then') {
                    index++;
                }

                if (index < formula.length && formula[index] === 'then') {
                    index++;
                    // Handle then case - find function (max/min)
                    if (['max', 'min'].includes(formula[index])) {
                        const func = formula[index];
                        index++;

                        // Find opening parenthesis
                        while (index < formula.length && formula[index] !== '(') {
                            index++;
                        }
                        index++; // Skip '('

                        // Collect expressions until closing parenthesis
                        const expressions = [];
                        let currentExpr = '';
                        let parenCount = 1;

                        while (index < formula.length && parenCount > 0) {
                            if (formula[index] === '(') parenCount++;
                            else if (formula[index] === ')') parenCount--;

                            if (formula[index] === ',' && parenCount === 1) {
                                expressions.push(currentExpr);
                                currentExpr = '';
                            } else if (parenCount > 0) {
                                currentExpr += formula[index] + ' ';
                            }
                            index++;
                        }
                        if (currentExpr) expressions.push(currentExpr);

                        if (conditionMet) {
                            return evaluateMinMax(func, expressions);
                        }
                    }
                }

                // If condition false, find else-if or else token
                while (index < formula.length &&
                    formula[index] !== 'else if' &&
                    formula[index] !== 'else') {
                    index++;
                }

                if (index < formula.length) {
                    if (formula[index] === 'else if') {
                        // Handle else-if case (recursive)
                        const remainingFormula = formula.slice(index);
                        return evaluateConditionalFormula(remainingFormula, values, calculatedValues);
                    } else if (formula[index] === 'else') {
                        index++;
                        // Skip 'then' if it exists after else
                        if (index < formula.length && formula[index] === 'then') {
                            index++;
                        }

                        // Handle else case
                        if (['max', 'min'].includes(formula[index])) {
                            const func = formula[index];
                            index++;

                            // Find opening parenthesis
                            while (index < formula.length && formula[index] !== '(') {
                                index++;
                            }
                            index++; // Skip '('

                            // Collect expressions until closing parenthesis
                            const expressions = [];
                            let currentExpr = '';
                            let parenCount = 1;

                            while (index < formula.length && parenCount > 0) {
                                if (formula[index] === '(') parenCount++;
                                else if (formula[index] === ')') parenCount--;

                                if (formula[index] === ',' && parenCount === 1) {
                                    expressions.push(currentExpr);
                                    currentExpr = '';
                                } else if (parenCount > 0) {
                                    currentExpr += formula[index] + ' ';
                                }
                                index++;
                            }
                            if (currentExpr) expressions.push(currentExpr);

                            if (!conditionMet) {
                                return evaluateMinMax(func, expressions);
                            }
                        }
                    }
                }
            }
        }
        index++;
    }

    // Default return if no matching condition found
    return 0;
};

// Create a separate, reusable function for getting unique HSCode count
export const getUniqueHSCodeCount = (items) => {
    const uniqueHSCodes = new Set(
        items.map((item) => item.itemHSCode || item.hsCode).filter(Boolean)
    );
    return uniqueHSCodes.size;
};

export const evaluateComplexFormula = (
    formula,
    values,
    calculatedValues,
    totalItemsValue,
    totalDimensionsWeight,
    totalDimensionsLength,
    totalDimensionsWidth,
    totalDimensionsHeight,
    cif,
    insurance,
    totalItemsQuantity,
    itemsTotalQty
) => {
    // console.log('Starting evaluateComplexFormula with:', {
    //   formula,
    //   values: JSON.stringify(values),
    //   calculatedValues: JSON.stringify(calculatedValues)
    // });

    const tokens = formula.flat();  // Flatten the array in case it's nested

    // console.log('Flattened tokens:', tokens);

    const precedence = {
        '*': 2,
        '/': 2,
        '+': 1,
        '-': 1,
        '%': 2
    };

    const applyOp = (op, b, a) => {

        // console.log(`Applying operation: ${a} ${op} ${b}`);

        switch (op) {
            case '+': return a + b;
            case '-': return a - b;
            case '*': return a * b;
            case '/': return b !== 0 ? a / b : Infinity;
            case '%': return (a * b) / 100;
        }
    };

    const getDynamicValue = (valueKey) => {
        switch (valueKey) {
            case "shipment value":
            case "Invoice Value":
            case "Shipment Value":
                return totalItemsValue;
            case "shipment weight":
            case "Item Weight":
                return totalDimensionsWeight;
            case "Quantity":
                return totalItemsQuantity;
            case "Total Item Quantity":
                return itemsTotalQty;
            case "Volume Weight":
                const totalVolumetricWeight = calculateTotalVolumetricWeight(values.dimensions);
                // console.log(totalVolumetricWeight, 'totalVolumetricWeight');
                return Math.max(totalDimensionsWeight, totalVolumetricWeight);
            case "Airfreight Volume":
                return calculateTotalVolumetricWeight(values.dimensions);
            case "Airfreight Charges":
                const chargeableWeight = calculateTotalVolumetricWeight(values.dimensions);
                const volumeWeight = Math.max(totalDimensionsWeight, chargeableWeight);
                if (volumeWeight < 360) {
                    return 6.50 * volumeWeight + 250;
                } else if (volumeWeight >= 360 && volumeWeight <= 1080) {
                    return 5.00 * volumeWeight + 250;
                } else {
                    return 4.00 * volumeWeight + 250;
                }
            case "CIF":
                return cif;
            case "CIF110":
                return totalItemsValue * 1.10;
            case "Insurance":
                return insurance;
            case "IOR":
                // IOR formula: if document then max(7.5% Shipment Value, 950) else max(5% Shipment Value, 650)
                const isDocRequired = values.requiredDoc === 'Required';
                if (isDocRequired) {
                    return Math.max((totalItemsValue * 7.5) / 100, 950);
                } else {
                    return Math.max((totalItemsValue * 5) / 100, 650);
                }
            case "Handling Charges":
                // Handling Charges formula: if Chargeable Weight >= 360 then 250 else 200
                const totalVolumetricWeightForHandling = calculateTotalVolumetricWeight(values.dimensions);
                const chargeableWeightForHandling = Math.max(totalDimensionsWeight, totalVolumetricWeightForHandling);
                return chargeableWeightForHandling >= 360 ? 250 : 200;
            case "Administration":
                // Administration formula: if Shipment Value < 800 then 150 else if Shipment Value <= 10000 then 200 else 250
                if (totalItemsValue < 800) {
                    return 150;
                } else if (totalItemsValue <= 10000) {
                    return 200;
                } else {
                    return 250;
                }
            case "Customs Clearance A Class":
                // < 250 -> 250
                // < 2500 -> 350 (covers <1000 and <2500 ranges)
                // < 100000 -> 450 (covers <50000 and <100000 ranges)
                // >= 100000 -> 550 (covers <500000 and >=500000 ranges -> 550)
                if (totalItemsValue < 250) return 250;
                if (totalItemsValue < 2500) return 350;
                if (totalItemsValue < 100000) return 450;
                return 550;
            case "Handling A Class":
                if (totalItemsValue < 250) return 75;
                if (totalItemsValue < 1000) return 150;
                if (totalItemsValue < 2500) return 250;
                if (totalItemsValue < 50000) return 300;
                if (totalItemsValue < 500000) return 350;
                return 450;
            case "Administration A Class":
                if (totalItemsValue < 250) return 50;
                if (totalItemsValue < 1000) return 100;
                if (totalItemsValue < 2500) return 150;
                if (totalItemsValue < 500000) return 250;
                return 300;
            case "THC A Class":
                if (totalItemsValue < 1000) return 150;
                if (totalItemsValue < 2500) return 195;
                if (totalItemsValue < 50000) return 350;
                if (totalItemsValue < 100000) return 400;
                if (totalItemsValue < 500000) return 450;
                return 575;
            case "Customs Clearance B Class":
                if (totalItemsValue < 250) return 250;
                if (totalItemsValue < 1000) return 350;
                if (totalItemsValue < 2500) return 450;
                if (totalItemsValue < 100000) return 550;
                return 650;
            case "THC B Class":
                if (totalItemsValue < 1000) return 195;
                if (totalItemsValue < 2500) return 250;
                if (totalItemsValue < 50000) return 395;
                if (totalItemsValue < 500000) return 450;
                return 550;
            case "Handling B Class":
                if (totalItemsValue < 250) return 75;
                if (totalItemsValue < 1000) return 150;
                if (totalItemsValue < 50000) return 250;
                return 350;
            case "Administration B Class":
                if (totalItemsValue < 250) return 50;
                if (totalItemsValue < 1000) return 100;
                if (totalItemsValue < 50000) return 200;
                return 250;
            case "Customs Clearance C Class":
                if (totalItemsValue < 250) return 350;
                if (totalItemsValue < 2500) return 450;
                if (totalItemsValue < 100000) return 550;
                return 750;
            case "Handling C Class":
                if (totalItemsValue < 250) return 75;
                if (totalItemsValue < 1000) return 200;
                if (totalItemsValue < 50000) return 250;
                if (totalItemsValue < 100000) return 350;
                return 550;
            case "Administration C Class":
                if (totalItemsValue < 250) return 50;
                if (totalItemsValue < 1000) return 150;
                if (totalItemsValue < 50000) return 200;
                return 250;
            case "THC C Class":
                if (totalItemsValue < 1000) return 150;
                if (totalItemsValue < 2500) return 195;
                if (totalItemsValue < 50000) return 350;
                if (totalItemsValue < 100000) return 400;
                if (totalItemsValue < 500000) return 450;
                return 575;
            case "document":
                return values.requiredDoc === 'Required' ? true : false;
            case "handling":  // ADD THIS NEW CASE
                return values.handling === 'YES' ? true : false;
            default:
                return calculatedValues[valueKey] !== undefined ? calculatedValues[valueKey] : parseFloat(valueKey) || 0;
        }
    };

    const evaluate = () => {
        const values = [];
        const ops = [];

        // console.log('Starting evaluation of tokens');

        for (let i = 0; i < tokens.length; i++) {

            // console.log(`Processing token: ${tokens[i]}`);

            if (tokens[i] === '(') {

                // console.log('Pushing opening parenthesis');

                ops.push(tokens[i]);
            }
            else if (tokens[i] === ')') {

                // console.log('Closing parenthesis encountered');

                while (ops.length && ops[ops.length - 1] !== '(') {
                    let op = ops.pop();
                    let b = values.pop();
                    let a = values.pop();
                    values.push(applyOp(op, b, a));
                }
                ops.pop(); // Remove '('
                // console.log('Closed parenthesis block');
            }
            else if (['+', '-', '*', '/', '%'].includes(tokens[i])) {
                // console.log(`Operator found: ${tokens[i]}`);
                while (ops.length && precedence[ops[ops.length - 1]] >= precedence[tokens[i]]) {
                    let op = ops.pop();
                    let b = values.pop();
                    let a = values.pop();
                    values.push(applyOp(op, b, a));
                }
                ops.push(tokens[i]);
            }
            else {
                const dynamicValue = getDynamicValue(tokens[i]);
                values.push(dynamicValue);
            }

            // console.log('Current values stack:', values);
            // console.log('Current operators stack:', ops);
        }

        // console.log('Final evaluation of remaining operators');

        while (ops.length) {
            let op = ops.pop();
            let b = values.pop();
            let a = values.pop();
            values.push(applyOp(op, b, a));
            // console.log('After final operation - values:', values);
        }
        // console.log('Final result:', values[0]);

        return values[0];
    };

    return evaluate();
};

export const calculateServiceValue = (
    service,
    values,
    calculatedValues,
    totalItemsValue,
    totalDimensionsWeight,
    totalDimensionsLength,
    totalDimensionsWidth,
    totalDimensionsHeight,
    cif,
    insurance,
    totalItemsQuantity,
    itemsTotalQty
) => {
    // console.log('=== calculateServiceValue START ===');
    // console.log('Service description:', service.description);
    // console.log('Service values:', service.values);
    // console.log('=== calculateServiceValue START ===');

    // console.log('Starting calculateServiceValue with:', {
    //   service: JSON.stringify(service),
    //   values: JSON.stringify(values),
    //   calculatedValues: JSON.stringify(calculatedValues)
    // });

    const nomHsCodeConditionIndex = isNomService(service) ? findHsCodeStartsWithIndex(service.values) : -1;

    if (nomHsCodeConditionIndex !== -1) {
        const hsCodeMatches = matchesHsCodePrefixes(values.items, getNomHsCodePrefixes());
        const thenIndex = service.values.indexOf("then", nomHsCodeConditionIndex);
        const elseIndex = service.values.indexOf("else", thenIndex);
        const formulaStartIndex = hsCodeMatches ? thenIndex + 1 : elseIndex + 1;
        const formulaEndIndex = hsCodeMatches && elseIndex !== -1 ? elseIndex : service.values.length;

        if (formulaStartIndex > 0) {
            return calculateServiceValue(
                { values: service.values.slice(formulaStartIndex, formulaEndIndex) },
                values,
                calculatedValues,
                totalItemsValue,
                totalDimensionsWeight,
                totalDimensionsLength,
                totalDimensionsWidth,
                totalDimensionsHeight,
                cif,
                insurance,
                totalItemsQuantity,
                itemsTotalQty
            );
        }

        return 0;
    }

    if (service.values.includes('if') &&
        (service.values.includes('max') || service.values.includes('min')) &&
        (service.values.includes('then') || service.values.includes('else'))) {
        // console.log('📍 CALLING evaluateConditionalFormula for:', service.description);
        return evaluateConditionalFormula(service.values,
            values,
            calculatedValues,
            totalItemsValue,
            totalDimensionsWeight,
            totalDimensionsLength,
            totalDimensionsWidth,
            totalDimensionsHeight,
            cif,
            insurance,
            totalItemsQuantity,
            itemsTotalQty);
    }
    // Add this check in the calculateServiceValue function, after your existing checks for other formula types
    if (service.values.includes('if') && service.values.includes('document') && !service.values.includes('max') && !service.values.includes('min')) {
        // console.log('📍 CALLING evaluateSimpleCondition for:', service.description);
        // This seems to be a simple condition with direct value assignment
        return evaluateSimpleCondition(service.values,
            values,
            calculatedValues,
            totalItemsValue,
            totalDimensionsWeight,
            totalDimensionsLength,
            totalDimensionsWidth,
            totalDimensionsHeight,
            cif,
            insurance,
            totalItemsQuantity,
            itemsTotalQty);
    }

    if (!service || !service.values || service.values.length === 0) return 0;
    if (service.description === "Insurance" && values.insuranceSelected !== "YES") {
        return 0;
    }

    let result = 0;
    let operator = "+";
    let lastValue = 0;

    // Formulae Step 5 start:

    const evaluateComplexCondition = (formulaSlice) => {
        let index = 0;

        const getValue = (key) => {
            switch (key) {
                case 'shipment value':
                case 'Invoice Value':
                case 'Shipment Value':
                    return totalItemsValue;
                case 'shipment weight':
                case 'Item Weight':
                    return totalDimensionsWeight;
                case "Quantity":
                    return totalItemsQuantity;
                case "Total Item Quantity":
                    return itemsTotalQty;
                case "Volume Weight":
                    const totalVolumetricWeight = calculateTotalVolumetricWeight(values.dimensions);
                    // console.log(totalVolumetricWeight, 'totalVolumetricWeight');
                    return Math.max(totalDimensionsWeight, totalVolumetricWeight);
                case "Airfreight Volume":
                    return calculateTotalVolumetricWeight(values.dimensions);
                case "Airfreight Charges":
                    const chargeableWeight = calculateTotalVolumetricWeight(values.dimensions);
                    const volumeWeight = Math.max(totalDimensionsWeight, chargeableWeight);
                    if (volumeWeight < 360) {
                        return 6.50 * volumeWeight + 250;
                    } else if (volumeWeight >= 360 && volumeWeight <= 1080) {
                        return 5.00 * volumeWeight + 250;
                    } else {
                        return 4.00 * volumeWeight + 250;
                    }
                case "CIF":
                    return cif;
                case "CIF110":
                    return totalItemsValue * 1.10;
                case "Insurance":
                    return insurance;
                case "IOR":
                    // IOR formula: if document then max(7.5% Shipment Value, 950) else max(5% Shipment Value, 650)
                    const isDocRequired = values.requiredDoc === 'Required';
                    if (isDocRequired) {
                        return Math.max((totalItemsValue * 7.5) / 100, 950);
                    } else {
                        return Math.max((totalItemsValue * 5) / 100, 650);
                    }
                case "Handling Charges":
                    // Handling Charges formula: if Chargeable Weight >= 360 then 250 else 200
                    const totalVolumetricWeightForHandling = calculateTotalVolumetricWeight(values.dimensions);
                    const chargeableWeightForHandling = Math.max(totalDimensionsWeight, totalVolumetricWeightForHandling);
                    return chargeableWeightForHandling >= 360 ? 250 : 200;
                case "Administration":
                    // Administration formula: if Shipment Value < 800 then 150 else if Shipment Value <= 10000 then 200 else 250
                    if (totalItemsValue < 800) {
                        return 150;
                    } else if (totalItemsValue <= 10000) {
                        return 200;
                    } else {
                        return 250;
                    }
                case "Customs Clearance A Class":
                    if (totalItemsValue < 250) return 250;
                    if (totalItemsValue < 2500) return 350;
                    if (totalItemsValue < 100000) return 450;
                    return 550;
                case "Handling A Class":
                    if (totalItemsValue < 250) return 75;
                    if (totalItemsValue < 1000) return 150;
                    if (totalItemsValue < 2500) return 250;
                    if (totalItemsValue < 50000) return 300;
                    if (totalItemsValue < 500000) return 350;
                    return 450;
                case "Administration A Class":
                    if (totalItemsValue < 250) return 50;
                    if (totalItemsValue < 1000) return 100;
                    if (totalItemsValue < 2500) return 150;
                    if (totalItemsValue < 500000) return 250;
                    return 300;
                case "THC A Class":
                    if (totalItemsValue < 1000) return 150;
                    if (totalItemsValue < 2500) return 195;
                    if (totalItemsValue < 50000) return 350;
                    if (totalItemsValue < 100000) return 400;
                    if (totalItemsValue < 500000) return 450;
                    return 575;
                case "Customs Clearance B Class":
                    if (totalItemsValue < 250) return 250;
                    if (totalItemsValue < 1000) return 350;
                    if (totalItemsValue < 2500) return 450;
                    if (totalItemsValue < 100000) return 550;
                    return 650;
                case "THC B Class":
                    if (totalItemsValue < 1000) return 195;
                    if (totalItemsValue < 2500) return 250;
                    if (totalItemsValue < 50000) return 395;
                    if (totalItemsValue < 500000) return 450;
                    return 550;
                case "Handling B Class":
                    if (totalItemsValue < 250) return 75;
                    if (totalItemsValue < 1000) return 150;
                    if (totalItemsValue < 50000) return 250;
                    return 350;
                case "Administration B Class":
                    if (totalItemsValue < 250) return 50;
                    if (totalItemsValue < 1000) return 100;
                    if (totalItemsValue < 50000) return 200;
                    return 250;
                case "Customs Clearance C Class":
                    if (totalItemsValue < 250) return 350;
                    if (totalItemsValue < 2500) return 450;
                    if (totalItemsValue < 100000) return 550;
                    return 750;
                case "Handling C Class":
                    if (totalItemsValue < 250) return 75;
                    if (totalItemsValue < 1000) return 200;
                    if (totalItemsValue < 50000) return 250;
                    if (totalItemsValue < 100000) return 350;
                    return 550;
                case "Administration C Class":
                    if (totalItemsValue < 250) return 50;
                    if (totalItemsValue < 1000) return 150;
                    if (totalItemsValue < 50000) return 200;
                    return 250;
                case "THC C Class":
                    if (totalItemsValue < 1000) return 150;
                    if (totalItemsValue < 2500) return 195;
                    if (totalItemsValue < 50000) return 350;
                    if (totalItemsValue < 100000) return 400;
                    if (totalItemsValue < 500000) return 450;
                    return 575;
                case "HSCode Count":
                    return getUniqueHSCodeCount(values.items);
                case "document":
                    return values.requiredDoc === 'Required' ? true : false;
                case "handling":
                    return values.handling === 'YES' ? true : false;
                default:
                    return calculatedValues[key] !== undefined ? calculatedValues[key] : parseFloat(key) || 0;
            }
        };

        const evaluateCondition = (value1, operator, value2) => {
            switch (operator) {
                case '>': return value1 > value2;
                case '<': return value1 < value2;
                case '>=': return value1 >= value2;
                case '<=': return value1 <= value2;
                case '==': return value1 === value2;
                default: return false;
            }
        };

        const evaluateExpression = (startIndex) => {
            let result = getValue(formulaSlice[startIndex]);
            let i = startIndex + 1;

            while (i < formulaSlice.length && !['else', 'else if'].includes(formulaSlice[i])) {
                const operator = formulaSlice[i];
                i++;
                const value = getValue(formulaSlice[i]);
                i++;

                switch (operator) {
                    case '+': result += value; break;
                    case '-': result -= value; break;
                    case '*': result *= value; break;
                    case '/': result /= value !== 0 ? value : 1; break;
                    case '%': result = (result * value) / 100; break;
                }
            }

            return result;
        };

        while (index < formulaSlice.length) {
            if (formulaSlice[index] === 'if' || formulaSlice[index] === 'else if') {
                index++;
                const conditionType = formulaSlice[index];

                // Handle boolean conditions (document, handling)
                if (conditionType === 'document' || conditionType === 'handling') {
                    const conditionValue = getValue(conditionType);
                    index++;

                    // Find 'then'
                    while (index < formulaSlice.length && formulaSlice[index] !== 'then') {
                        index++;
                    }

                    if (formulaSlice[index] === 'then') {
                        index++; // Move past 'then'
                        const thenValue = getValue(formulaSlice[index]);

                        if (conditionValue === true) {
                            return thenValue;
                        }

                        // Condition is false, skip to else/else if
                        index++;
                        while (index < formulaSlice.length && formulaSlice[index] !== 'else if' && formulaSlice[index] !== 'else') {
                            index++;
                        }
                    }
                }
                // Handle HSCode Starts with
                else if (HS_CODE_STARTS_WITH_KEYS.includes(conditionType) || conditionType === 'HSCode') {
                    // Collect all prefixes until 'then'
                    const prefixes = [];
                    index++;

                    if (formulaSlice[index] === 'Starts with') {
                        index++; // Skip 'Starts with' if it appears
                    }

                    while (index < formulaSlice.length && formulaSlice[index] !== 'then') {
                        if (formulaSlice[index] !== ',') {
                            prefixes.push(formulaSlice[index]);
                        }
                        index++;
                    }

                    const hsCodeMatches = checkHSCodeStartsWith(values.items, prefixes);

                    if (formulaSlice[index] === 'then') {
                        index++; // Move past 'then'

                        if (hsCodeMatches) {
                            return evaluateExpression(index);
                        }

                        // No match, skip to else/else if
                        index++;
                        while (index < formulaSlice.length && formulaSlice[index] !== 'else if' && formulaSlice[index] !== 'else') {
                            index++;
                        }
                    }
                }
                // Handle numeric comparison conditions
                else {
                    const value1 = getValue(formulaSlice[index]);
                    index++;
                    const operator = formulaSlice[index];
                    index++;
                    const value2 = getValue(formulaSlice[index]);
                    index++;

                    if (evaluateCondition(value1, operator, value2)) {
                        index++; // Skip 'then'
                        return evaluateExpression(index);
                    } else {
                        // Skip to next 'else if' or 'else'
                        while (index < formulaSlice.length && formulaSlice[index] !== 'else if' && formulaSlice[index] !== 'else') {
                            index++;
                        }
                    }
                }
            } else if (formulaSlice[index] === 'else') {
                index++; // Skip 'else'
                if (formulaSlice[index] === 'then') {
                    index++; // Skip 'then'
                }
                return evaluateExpression(index);
            } else {
                index++;
            }
        }

        return null;
    };

    // Ending Step 5 Formulae.

    const checkHSCodeStartsWith = (items, prefixes) => {
        const prefixArray = service.description?.trim().toLowerCase() === "nom"
            ? getNomHsCodePrefixes()
            : (Array.isArray(prefixes) ? prefixes : [prefixes]);

        return matchesHsCodePrefixes(items, prefixArray);
    };

    const getDynamicValue = (valueKey) => {
        switch (valueKey) {
            case "Invoice Value":
            case "Shipment Value":
                return totalItemsValue;

            case "Item Weight":
                return totalDimensionsWeight;

            case "Volume Weight":
                const totalVolumetricWeight = calculateTotalVolumetricWeight(values.dimensions);
                // console.log(totalVolumetricWeight, 'totalVolumetricWeight');
                return Math.max(totalDimensionsWeight, totalVolumetricWeight);
            case "Airfreight Volume":
                return calculateTotalVolumetricWeight(values.dimensions);
            case "Airfreight Charges":
                const chargeableWeight = calculateTotalVolumetricWeight(values.dimensions);
                const volumeWeight = Math.max(totalDimensionsWeight, chargeableWeight);
                if (volumeWeight < 360) {
                    return 6.50 * volumeWeight + 250;
                } else if (volumeWeight >= 360 && volumeWeight <= 1080) {
                    return 5.00 * volumeWeight + 250;
                } else {
                    return 4.00 * volumeWeight + 250;
                }
            case "CIF":
                return cif;

            case "CIF110":
                return totalItemsValue * 1.10;

            case "Insurance":
                return insurance;

            case "IOR":
                // IOR formula: if document then max(7.5% Shipment Value, 950) else max(5% Shipment Value, 650)
                const isDocRequired = values.requiredDoc === 'Required';
                if (isDocRequired) {
                    return Math.max((totalItemsValue * 7.5) / 100, 950);
                } else {
                    return Math.max((totalItemsValue * 5) / 100, 650);
                }

            case "Handling Charges":
                // Handling Charges formula: if Chargeable Weight >= 360 then 250 else 200
                const totalVolumetricWeightForHandling = calculateTotalVolumetricWeight(values.dimensions);
                const chargeableWeightForHandling = Math.max(totalDimensionsWeight, totalVolumetricWeightForHandling);
                return chargeableWeightForHandling >= 360 ? 250 : 200;

            case "Administration":
                // Administration formula: if Shipment Value < 800 then 150 else if Shipment Value <= 10000 then 200 else 250
                if (totalItemsValue < 800) {
                    return 150;
                } else if (totalItemsValue <= 10000) {
                    return 200;
                } else {
                    return 250;
                }

            case "Customs Clearance A Class":
                if (totalItemsValue < 250) return 250;
                if (totalItemsValue < 2500) return 350;
                if (totalItemsValue < 100000) return 450;
                return 550;

            case "Handling A Class":
                if (totalItemsValue < 250) return 75;
                if (totalItemsValue < 1000) return 150;
                if (totalItemsValue < 2500) return 250;
                if (totalItemsValue < 50000) return 300;
                if (totalItemsValue < 500000) return 350;
                return 450;

            case "Administration A Class":
                if (totalItemsValue < 250) return 50;
                if (totalItemsValue < 1000) return 100;
                if (totalItemsValue < 2500) return 150;
                if (totalItemsValue < 500000) return 250;
                return 300;

            case "THC A Class":
                if (totalItemsValue < 1000) return 150;
                if (totalItemsValue < 2500) return 195;
                if (totalItemsValue < 50000) return 350;
                if (totalItemsValue < 100000) return 400;
                if (totalItemsValue < 500000) return 450;
                return 575;

            case "Customs Clearance B Class":
                if (totalItemsValue < 250) return 250;
                if (totalItemsValue < 1000) return 350;
                if (totalItemsValue < 2500) return 450;
                if (totalItemsValue < 100000) return 550;
                return 650;

            case "THC B Class":
                if (totalItemsValue < 1000) return 195;
                if (totalItemsValue < 2500) return 250;
                if (totalItemsValue < 50000) return 395;
                if (totalItemsValue < 500000) return 450;
                return 550;

            case "Handling B Class":
                if (totalItemsValue < 250) return 75;
                if (totalItemsValue < 1000) return 150;
                if (totalItemsValue < 50000) return 250;
                return 350;

            case "Administration B Class":
                if (totalItemsValue < 250) return 50;
                if (totalItemsValue < 1000) return 100;
                if (totalItemsValue < 50000) return 200;
                return 250;

            case "Customs Clearance C Class":
                if (totalItemsValue < 250) return 350;
                if (totalItemsValue < 2500) return 450;
                if (totalItemsValue < 100000) return 550;
                return 750;

            case "Handling C Class":
                if (totalItemsValue < 250) return 75;
                if (totalItemsValue < 1000) return 200;
                if (totalItemsValue < 50000) return 250;
                if (totalItemsValue < 100000) return 350;
                return 550;

            case "Administration C Class":
                if (totalItemsValue < 250) return 50;
                if (totalItemsValue < 1000) return 150;
                if (totalItemsValue < 50000) return 200;
                return 250;

            case "THC C Class":
                if (totalItemsValue < 1000) return 150;
                if (totalItemsValue < 2500) return 195;
                if (totalItemsValue < 50000) return 350;
                if (totalItemsValue < 100000) return 400;
                if (totalItemsValue < 500000) return 450;
                return 575;

            case "Quantity":
                return totalItemsQuantity;

            case "Total Item Quantity":
                return itemsTotalQty;

            case "HSCode Count":
                return getUniqueHSCodeCount(values.items);
            default:
                // Check if it's a reference to another service
                if (calculatedValues[valueKey] !== undefined) {
                    return calculatedValues[valueKey];
                }
                // If it's not a special keyword, treat it as a numeric value
                return parseFloat(valueKey) || 0;
        }
    };

    const evaluateIfCondition = (values, index) => {
        const value1Key = values[index + 1];
        const operator = values[index + 2];
        const value2Key = values[index + 3];
        const thenKeyword = values[index + 4];
        const thenValue = values[index + 5];

        const value1 = getDynamicValue(value1Key);
        const value2 = getDynamicValue(value2Key);

        let conditionMet = false;
        switch (operator) {
            case ">":
                conditionMet = value1 > value2;
                break;
            case "<":
                conditionMet = value1 < value2;
                break;
            case ">=":
                conditionMet = value1 >= value2;
                break;
            case "<=":
                conditionMet = value1 <= value2;
                break;
            case "==":
                conditionMet = value1 === value2;
                break;
        }

        if (conditionMet && thenKeyword === "then") {
            const result = getDynamicValue(thenValue);

            return result;
        } else {
            return null;
        }
    };

    const evaluateExpression = (expressionArray) => {
        let localResult = 1; // Initialize to 1 for multiplication
        let localOperator = "*"; // Start with multiplication

        for (let j = 0; j < expressionArray.length; j++) {
            let item = expressionArray[j];

            // Handle multi-word dynamic values
            if (item === "Invoice" && expressionArray[j + 1] === "Value") {
                item = "Invoice Value";
                j++; // Skip the next item
            }
            if (item === "Item" && expressionArray[j + 1] === "Weight") {
                item = "Item Weight";
                j++; // Skip the next item
            }
            if (item === "Quantity" && expressionArray[j + 1] === "Quantity") {
                item = "Quantity";
                j++;
            }
            if (item === "Total Item Quantity" && expressionArray[j + 1] === "Quantity") {
                item = "Total Item Quantity";
                j++;
            }
            if (item === "Volume" && expressionArray[j + 1] === "Weight") {
                item = "Volume Weight";
                j++; // Skip the next item
            }
            if (item === "Airfreight" && expressionArray[j + 1] === "Volume") {
                item = "Airfreight Volume";
                j++; // Skip the next item
            }
            if (item === "Airfreight" && expressionArray[j + 1] === "Charges") {
                item = "Airfreight Charges";
                j++; // Skip the next item
            }
            if (item === "CIF" && expressionArray[j + 1] === "CIF") {
                item = "CIF";
                j++; // Skip the next item
            }
            if (item === "CIF110" && expressionArray[j + 1] === "CIF110") {
                item = "CIF110";
                j++; // Skip the next item
            }
            if (item === "Insurance" && expressionArray[j + 1] === "Insurance") {
                item = "Insurance";
                j++; // Skip the next item
            }
            if (item === "IOR" && expressionArray[j + 1] === "IOR") {
                item = "IOR";
                j++; // Skip the next item
            }
            if (item === "Handling" && expressionArray[j + 1] === "Charges") {
                item = "Handling Charges";
                j++; // Skip the next item
            }
            if (item === "Administration" && expressionArray[j + 1] === "Administration") {
                item = "Administration";
                j++; // Skip the next item
            }
            if (item === "Customs" && expressionArray[j + 1] === "Clearance" && expressionArray[j + 2] === "A" && expressionArray[j + 3] === "Class") {
                item = "Customs Clearance A Class";
                j += 3; // Skip the next three items
            }
            if (item === "Handling" && expressionArray[j + 1] === "A" && expressionArray[j + 2] === "Class") {
                item = "Handling A Class";
                j += 2; // Skip the next two items
            }
            if (item === "Administration" && expressionArray[j + 1] === "A" && expressionArray[j + 2] === "Class") {
                item = "Administration A Class";
                j += 2; // Skip the next two items
            }
            if (item === "Customs" && expressionArray[j + 1] === "Clearance" && expressionArray[j + 2] === "B" && expressionArray[j + 3] === "Class") {
                item = "Customs Clearance B Class";
                j += 3; // Skip the next three items
            }
            if (item === "THC" && expressionArray[j + 1] === "A" && expressionArray[j + 2] === "Class") {
                item = "THC A Class";
                j += 2; // Skip the next two items
            }
            if (item === "THC" && expressionArray[j + 1] === "B" && expressionArray[j + 2] === "Class") {
                item = "THC B Class";
                j += 2; // Skip the next two items
            }
            if (item === "Handling" && expressionArray[j + 1] === "B" && expressionArray[j + 2] === "Class") {
                item = "Handling B Class";
                j += 2; // Skip the next two items
            }
            if (item === "Administration" && expressionArray[j + 1] === "B" && expressionArray[j + 2] === "Class") {
                item = "Administration B Class";
                j += 2; // Skip the next two items
            }
            if (item === "Customs" && expressionArray[j + 1] === "Clearance" && expressionArray[j + 2] === "C" && expressionArray[j + 3] === "Class") {
                item = "Customs Clearance C Class";
                j += 3; // Skip the next three items
            }
            if (item === "Handling" && expressionArray[j + 1] === "C" && expressionArray[j + 2] === "Class") {
                item = "Handling C Class";
                j += 2; // Skip the next two items
            }
            if (item === "Administration" && expressionArray[j + 1] === "C" && expressionArray[j + 2] === "Class") {
                item = "Administration C Class";
                j += 2; // Skip the next two items
            }
            if (item === "THC" && expressionArray[j + 1] === "C" && expressionArray[j + 2] === "Class") {
                item = "THC C Class";
                j += 2; // Skip the next two items
            }
            if (item === "HSCode" && expressionArray[j + 1] === "Count") {
                item = "HSCode Count";
                j++; // Skip the next item
            }
            if (item === "," || item === "(" || item === ")") continue;

            if (["*", "/", "+", "-", "%"].includes(item)) {
                localOperator = item;
            } else {
                const value = getDynamicValue(item);
                // console.log(`Item: ${item}, Value: ${value}, Operator: ${localOperator}`);// Debugging log
                switch (localOperator) {
                    case "+": localResult += value; break;
                    case "-": localResult -= value; break;
                    case "*": localResult *= value; break;
                    case "/": localResult /= value !== 0 ? value : 1; break;
                    case "%":
                        if (localResult === 1) {
                            // For percentage calculations, use the first value as the base
                            localResult = (value * localResult) / 100;
                        } else {
                            localResult = (localResult * value) / 100;
                        }
                        break;
                }
            }
        }
        // console.log(`Expression result: ${localResult}`); // Debugging log
        return localResult;
    };

    const evaluateHSCodeCondition = (condition, count) => {
        const [operator, value] = condition.split(" ");
        switch (operator) {
            case ">":
                return count > Number(value);
            case "<":
                return count < Number(value);
            case ">=":
                return count >= Number(value);
            case "<=":
                return count <= Number(value);
            case "==":
                return count === Number(value);
            default:
                return false;
        }
    };

    for (let i = 0; i < service.values.length; i++) {
        const item = service.values[i];

        // console.log(`Processing item at index ${i}: ${item}`);
        if (typeof item === 'string' && (item.includes("A Class") || item.includes("B Class"))) {
            console.log(`DEBUG: Processing item: '${item}'`, "totalItemsValue:", totalItemsValue);
        }

        if (item === "if") {
            const complexResult = evaluateComplexCondition(service.values.slice(i));
            if (complexResult !== null) {
                return complexResult;
            }
        }

        if (item === "min" || item === "max") {
            // console.log(`Processing ${item} expression`); // Debugging log
            const expressionStart = service.values.indexOf("(", i);
            const expressionEnd = service.values.lastIndexOf(")");
            if (expressionStart !== -1 && expressionEnd !== -1) {
                const expressionArray = service.values.slice(expressionStart + 1, expressionEnd);
                const expressions = expressionArray.join(' ').split(',').map(expr => expr.trim());
                // console.log(`Expressions to evaluate: ${JSON.stringify(expressions)}`); // Debugging log
                const results = expressions.map(expr => evaluateExpression(expr.split(' ')));
                // console.log(`Results: ${JSON.stringify(results)}`); // Debugging log
                result = item === "min"
                    ? Math.min(...results)
                    : Math.max(...results);
                return Number(result.toFixed(2)); // Round to 2 decimal places
            }
        }

        if (service.values.includes('(')) {
            return evaluateComplexFormula(service.values, values, calculatedValues);
        }

        if (item === "if") {
            // const ifResult = evaluateIfCondition(service.values, i);
            // if (ifResult !== null) {
            //   return ifResult;
            // }

            const condition = service.values[i + 1];
            const value = service.values[i + 2];
            const keyword = service.values[i + 3];

            if (HS_CODE_STARTS_WITH_KEYS.includes(condition)) {
                // Support both single and multiple HSCode prefixes
                const prefixes = [];
                let j = i + 2;
                while (j < service.values.length && service.values[j] !== 'then') {
                    if (service.values[j] !== ',') {
                        prefixes.push(service.values[j]);
                    }
                    j++;
                }

                // console.log('HSCode Starts with prefixes:', prefixes);
                const hsCodeMatches = checkHSCodeStartsWith(values.items, prefixes);

                if (hsCodeMatches) {
                    // Find the 'then' index and process the formula
                    const thenIndex = service.values.indexOf('then', i);
                    const elseIndex = service.values.indexOf('else', thenIndex);
                    const formulaEndIndex = elseIndex !== -1 ? elseIndex : service.values.length;

                    const subFormula = {
                        values: service.values.slice(thenIndex + 1, formulaEndIndex),
                    };

                    return calculateServiceValue(subFormula, values, calculatedValues);
                } else if (service.values.includes('else')) {
                    // Process else clause if no match and else exists
                    const elseIndex = service.values.indexOf('else');
                    const subFormula = {
                        values: service.values.slice(elseIndex + 1),
                    };

                    return calculateServiceValue(subFormula, values, calculatedValues);
                }

                // If no match and no else clause, return 0
                return 0;
            }
        }

        if (item === "HSCode Count") {
            const condition = service.values[i + 1] + " " + service.values[i + 2];
            const keyword = service.values[i + 3];
            const hsCodeCount = getUniqueHSCodeCount(values.items);

            if (evaluateHSCodeCondition(condition, hsCodeCount)) {
                const formulaStartIndex = i + 4;
                const elseIndex = service.values.indexOf("else", formulaStartIndex);
                const formulaEndIndex =
                    elseIndex !== -1 ? elseIndex : service.values.length;

                const subFormula = {
                    values: service.values.slice(formulaStartIndex, formulaEndIndex),
                };

                return calculateServiceValue(subFormula, values, calculatedValues);
            } else if (service.values.includes("else")) {
                const elseIndex = service.values.indexOf("else");
                const subFormula = {
                    values: service.values.slice(elseIndex + 1),
                };

                return calculateServiceValue(subFormula, values, calculatedValues);
            }

            return 0;
        }

        if (item === "COO") {
            // Handle COO logic
            const country = service.values[i + 1];
            const keyword = service.values[i + 2];
            const matchingItem = values.items.find(
                (item) => item.countryOrigin === country
            );

            if (matchingItem) {
                // If country matches, use the formula after the keyword
                const formulaStartIndex = i + 3;
                const elseIndex = service.values.indexOf("else", formulaStartIndex);
                const formulaEndIndex =
                    elseIndex !== -1 ? elseIndex : service.values.length;

                const subFormula = {
                    values: service.values.slice(formulaStartIndex, formulaEndIndex),
                };

                result = calculateServiceValue(subFormula, values, calculatedValues);
            } else if (service.values.includes("else")) {
                // If country doesn't match and there's an else clause, use the else part
                const elseIndex = service.values.indexOf("else");
                const subFormula = {
                    values: service.values.slice(elseIndex + 1),
                };

                result = calculateServiceValue(subFormula, values, calculatedValues);
            }

            // Skip to the end of the COO block
            return result;
        }

        if (i % 2 === 0) {
            // It's a value or variable
            let numValue;
            // console.log(`Calculating value for: ${item}`);
            // console.log(insurance, "-=-=-=-Insurance");


            switch (item) {
                case "Invoice Value":
                case "Shipment Value":
                    numValue = totalItemsValue;
                    break;
                case "Item Weight": numValue = totalDimensionsWeight; break;
                case "Volume Weight":
                    const totalVolumetricWeight = calculateTotalVolumetricWeight(values.dimensions);
                    // console.log(totalVolumetricWeight, 'totalVolumetricWeight');
                    numValue = Math.max(totalDimensionsWeight, totalVolumetricWeight);
                    break;
                case "Airfreight Volume":
                    numValue = calculateTotalVolumetricWeight(values.dimensions);
                    break;
                case "Airfreight Charges":
                    const chargeableWeight = calculateTotalVolumetricWeight(values.dimensions);
                    const volumeWeight = Math.max(totalDimensionsWeight, chargeableWeight);
                    if (volumeWeight < 360) {
                        numValue = 6.50 * volumeWeight + 250;
                        break;
                    } else if (volumeWeight >= 360 && volumeWeight <= 1080) {
                        numValue = 5.00 * volumeWeight + 250;
                        break;
                    } else {
                        numValue = 4.00 * volumeWeight + 250;
                        break;
                    }
                case "CIF":
                    numValue = cif;
                    break;
                case "CIF110":
                    numValue = totalItemsValue * 1.10;
                    break;
                case "Insurance":
                    numValue = insurance;
                    // console.log("numValue = insurance", insurance, " ", formData.insuranceSelected);
                    break;
                case "IOR":
                    // IOR formula: if document then max(7.5% Shipment Value, 950) else max(5% Shipment Value, 650)
                    const isDocRequired = values.requiredDoc === 'Required';
                    if (isDocRequired) {
                        numValue = Math.max((totalItemsValue * 7.5) / 100, 950);
                    } else {
                        numValue = Math.max((totalItemsValue * 5) / 100, 650);
                    }
                    break;
                case "Handling Charges":
                    // Handling Charges formula: if Chargeable Weight >= 360 then 250 else 200
                    const totalVolumetricWeightForHandling = calculateTotalVolumetricWeight(values.dimensions);
                    const chargeableWeightForHandling = Math.max(totalDimensionsWeight, totalVolumetricWeightForHandling);
                    numValue = chargeableWeightForHandling >= 360 ? 250 : 200;
                    break;
                case "Administration":
                    // Administration formula: if Shipment Value < 800 then 150 else if Shipment Value <= 10000 then 200 else 250
                    if (totalItemsValue < 800) {
                        numValue = 150;
                    } else if (totalItemsValue <= 10000) {
                        numValue = 200;
                    } else {
                        numValue = 250;
                    }
                    break;
                case "Customs Clearance A Class":
                    if (totalItemsValue < 250) numValue = 250;
                    else if (totalItemsValue < 2500) numValue = 350;
                    else if (totalItemsValue < 100000) numValue = 450;
                    else numValue = 550;
                    break;
                case "Handling A Class":
                    if (totalItemsValue < 250) numValue = 75;
                    else if (totalItemsValue < 1000) numValue = 150;
                    else if (totalItemsValue < 2500) numValue = 250;
                    else if (totalItemsValue < 50000) numValue = 300;
                    else if (totalItemsValue < 500000) numValue = 350;
                    else numValue = 450;
                    break;
                case "Administration A Class":
                    if (totalItemsValue < 250) numValue = 50;
                    else if (totalItemsValue < 1000) numValue = 100;
                    else if (totalItemsValue < 2500) numValue = 150;
                    else if (totalItemsValue < 500000) numValue = 250;
                    else numValue = 300;
                    break;
                case "Customs Clearance B Class":
                    if (totalItemsValue < 250) numValue = 250;
                    else if (totalItemsValue < 1000) numValue = 350;
                    else if (totalItemsValue < 2500) numValue = 450;
                    else if (totalItemsValue < 100000) numValue = 550;
                    else numValue = 650;
                    break;
                case "THC A Class":
                    if (totalItemsValue < 1000) numValue = 150;
                    else if (totalItemsValue < 2500) numValue = 195;
                    else if (totalItemsValue < 50000) numValue = 350;
                    else if (totalItemsValue < 100000) numValue = 400;
                    else if (totalItemsValue < 500000) numValue = 450;
                    else numValue = 575;
                    break;
                case "THC B Class":
                    if (totalItemsValue < 1000) numValue = 195;
                    else if (totalItemsValue < 2500) numValue = 250;
                    else if (totalItemsValue < 50000) numValue = 395;
                    else if (totalItemsValue < 500000) numValue = 450;
                    else numValue = 550;
                    break;
                case "Handling B Class":
                    if (totalItemsValue < 250) numValue = 75;
                    else if (totalItemsValue < 1000) numValue = 150;
                    else if (totalItemsValue < 50000) numValue = 250;
                    else numValue = 350;
                    break;
                case "Administration B Class":
                    if (totalItemsValue < 250) numValue = 50;
                    else if (totalItemsValue < 1000) numValue = 100;
                    else if (totalItemsValue < 50000) numValue = 200;
                    else numValue = 250;
                    break;
                case "Customs Clearance C Class":
                    if (totalItemsValue < 250) numValue = 350;
                    else if (totalItemsValue < 2500) numValue = 450;
                    else if (totalItemsValue < 100000) numValue = 550;
                    else numValue = 750;
                    break;
                case "Handling C Class":
                    if (totalItemsValue < 250) numValue = 75;
                    else if (totalItemsValue < 1000) numValue = 200;
                    else if (totalItemsValue < 50000) numValue = 250;
                    else if (totalItemsValue < 100000) numValue = 350;
                    else numValue = 550;
                    break;
                case "Administration C Class":
                    if (totalItemsValue < 250) numValue = 50;
                    else if (totalItemsValue < 1000) numValue = 150;
                    else if (totalItemsValue < 50000) numValue = 200;
                    else numValue = 250;
                    break;
                case "THC C Class":
                    if (totalItemsValue < 1000) numValue = 150;
                    else if (totalItemsValue < 2500) numValue = 195;
                    else if (totalItemsValue < 50000) numValue = 350;
                    else if (totalItemsValue < 100000) numValue = 400;
                    else if (totalItemsValue < 500000) numValue = 450;
                    else numValue = 575;
                    break;
                case "Administration Yellow":
                    if (totalItemsValue < 250) numValue = 50;
                    else if (totalItemsValue < 1000) numValue = 100;
                    else if (totalItemsValue < 50000) numValue = 200;
                    else numValue = 250;
                    break;
                case "Quantity":
                    numValue = totalItemsQuantity;
                    break;
                case "Total Item Quantity":
                    numValue = itemsTotalQty;
                    break;
                case "HSCode Count":
                    numValue = getUniqueHSCodeCount(values.items);
                    break;
                default:
                    // Check if it's a reference to another service
                    if (calculatedValues[item]) {
                        numValue = calculatedValues[item];
                    } else {
                        numValue = parseFloat(item) || 0;
                    }
                    break;
            }

            // console.log(`Calculated numValue for ${item}: ${numValue}`);

            switch (operator) {
                case "+":
                    result += numValue;
                    break;
                case "-":
                    result -= numValue;
                    break;
                case "*":
                    result *= numValue;
                    break;
                case "/":
                    result /= numValue !== 0 ? numValue : 1; // Avoid division by zero
                    break;
                case ">":
                    lastValue = numValue;
                    break;
                case "%":
                    result = (result * numValue) / 100;
                    break;
            }
            // console.log(`After ${operator} operation, result: ${result}`);
        } else {
            // It's an operator
            operator = item;
            // console.log(`Set new operator: ${operator}`);
        }
    }

    if (operator === ">" && result < lastValue) {
        // console.log(`Applying > condition: setting result to ${lastValue}`);
        result = lastValue;
    }

    // console.log(`Final result for service calculation: ${result}`);

    return result;
};
